import PropTypes from 'prop-types'

import {List, Map} from 'immutable'
import {useMemo, useState} from 'react'
import {connect} from 'react-redux'

import AddDeputyController from './AddDeputyController'
import CancelDeputy from './CancelDeputy'
import DeputyAddedConfirmation from './DeputyAddedConfirmation'
import MediumModal from '../../../../../shared_components/mui_base_components/modals/medium_modal/MediumModal'
import PremiumAccessDenied from '../../../../../shared_components/PremiumAccessDenied'

import {
  useScoreLedgerEventLogger
} from '../../../../../lib/ScoreLedgerEventLogger'

import {deputizableDomainEmails} from '../../../../../lib/emailTools'

import {
  findRawResourceByAttributeId,
  findRawResourceById,
  findResourceById
} from '../../../../../lib/plan_data/userData'

import './addDeputyModal.scss'

export const AddDeputyModal = ({clientFirstName, draftDeputy, ...otherProps}) => {
  const isDraftDeputy = useMemo(() => (
    draftDeputy && !draftDeputy.isEmpty()
  ), [draftDeputy])

  const source = otherProps.source ||
    otherProps.location?.query?.source ||
    'planholder'

  // Overrides the context used for Mixpanel tracking only, when the flow was initiated
  // from a widget (like the Sharing module) that should report its own context
  // regardless of the page it's rendered on.
  const mixpanelContext = otherProps.mixpanelContext || otherProps.context

  const {logWithNotification} = useScoreLedgerEventLogger(otherProps.userConfig)

  const [
    showDeputyAddedConfirmation, setShowDeputyAddedConfirmation
  ] = useState(false)

  const [showWarning, setShowWarning] = useState(false)
  const [refetchQueriesCallback, setRefetchQueriesCallback] = useState(null)

  const contactFromId = findResourceById({
    resourceList: otherProps.newContacts,
    id: otherProps.params?.contactId
  })

  const contact = (otherProps.selectedContact && otherProps.selectedContact.size) ?
    otherProps.selectedContact :
    contactFromId

  const initialDeputyState = () => {
    if (contact && contact.size) {
      return {
        'first-name': contact.get('first-name', ''),
        'last-name': contact.get('last-name', ''),
        email: deputizableDomainEmails({
          contact,
          organization: otherProps.organization
        }).first() || ''
      }
    }

    if (isDraftDeputy) {
      const permissionsConfig = draftDeputy.get('permissions-config')
      const permissions = permissionsConfig.delete('selectAll')

      return {
        'death-reporter': draftDeputy.getIn([
          'deputy-config', 'death-reporter'
        ], false),
        'death-verification-period': draftDeputy.getIn([
          'deputy-config', 'death-verification-period'
        ], 2880),
        'deputy-relationship': {
          value: draftDeputy.getIn(['deputy-config', 'deputy-relationship'], '')
        },
        inviteText: draftDeputy.get('custom-invite-text', ''),
        'first-name': draftDeputy.get('first-name', ''),
        'last-name': draftDeputy.get('last-name', ''),
        email: draftDeputy.get('email', ''),
        permissions: permissions.toJS()
      }
    }
    return {
      'first-name': '',
      'last-name': '',
      email: ''
    }
  }

  const [deputy, setDeputy] = useState(initialDeputyState())

  const toggleWarning = () => { setShowWarning(!showWarning) }

  const warningModal = () => {
    if (showWarning) {
      return (
        <CancelDeputy
          context={otherProps.context}
          hideWarning={toggleWarning}
          exit={otherProps.exit}
        />
      )
    }
  }

  const handleSubmitCompleted = callback => {
    setShowDeputyAddedConfirmation(true)

    otherProps.context === 'dashboard' ?
      logWithNotification.addedDeputyFromPrompt() :
      logWithNotification.addedDeputy()

    if (callback)
      setRefetchQueriesCallback(() => callback)
  }

  const handleDeputyAddedConfirmationModalClose = () => {
    if (refetchQueriesCallback)
      refetchQueriesCallback()

    if (otherProps.onDraftDeputyReviewed)
      otherProps.onDraftDeputyReviewed()

    otherProps.exit()
  }

  if (otherProps.userConfig.get('freemium')) {
    return <PremiumAccessDenied exit={otherProps.exit} />
  } else if (showDeputyAddedConfirmation) {
    return (
      <DeputyAddedConfirmation
        clientFirstName={clientFirstName}
        context={otherProps.context}
        exit={handleDeputyAddedConfirmationModalClose}
        firstName={deputy['first-name']}
        mixpanelContext={mixpanelContext}
        source={source}
      />
    )
  }

  return (
    <div className='add-deputy-modal'>
      <MediumModal
        onClose={deputy['first-name'] ? toggleWarning : otherProps.exit}
        open>
        <AddDeputyController
          deputy={deputy}
          isDraftDeputy={isDraftDeputy}
          setDeputy={setDeputy}
          handleSubmitCompleted={handleSubmitCompleted}
          {...otherProps}
        />
      </MediumModal>
      {warningModal()}
    </div>
  )
}

AddDeputyModal.propTypes = {
  clientFirstName: PropTypes.string,
  context: PropTypes.string.isRequired,
  draftDeputy: PropTypes.instanceOf(Map),
  exit: PropTypes.func,
  mixpanelContext: PropTypes.string,
  newContacts: PropTypes.instanceOf(List),
  onDraftDeputyReviewed: PropTypes.func,
  organization: PropTypes.instanceOf(Map),
  params: PropTypes.shape({contactId: PropTypes.string}),
  source: PropTypes.string,
  selectedContact: PropTypes.instanceOf(Map),
  userConfig: PropTypes.instanceOf(Map)
}

const mapStateToProps = (state, {userConfig}) => {
  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })

  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: userConfig.get('firm-id') || firmOwnership.get('ownable-id')
  })

  const organization = findRawResourceById({
    rawResource: state.api.organizations,
    id: firm.get('organization-id')
  })

  return {organization}
}

export default connect(mapStateToProps)(AddDeputyModal)
